import { useMemo, useState } from "react";

import bbox from "@turf/bbox";

import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import TerritoryLevel from "@mapmycustomers/shared/enum/TerritoryLevel";
import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import LevelAreaRegions from "@mapmycustomers/shared/types/territory/LevelAreaRegions";
import { convertPlatformBoundsToLatLngBounds } from "@mapmycustomers/shared/util/geo/GeoService";

import { useMap, useMapEventHandler } from "@app/component/map/FeaturedMap";
import useAreaTypeMetaData from "@app/scene/map/components/TerritoriesMode/utils/useAreaTypeMetaData";
import useBoundaries from "@app/scene/map/components/TerritoriesMode/utils/useBoundaries";
import MapViewportState from "@app/types/map/MapViewportState";

const defaultThresholdLevels: [adm2Threshold: number, adm3Threshold: number] = [6, 9];

const filterVisibleRegions = (visibleBounds: google.maps.LatLngBounds) => (item: AreaRegion) => {
  const [minLon, minLat, maxLon, maxLat] = bbox(item.geometry);
  const bounds = new google.maps.LatLngBounds(
    { lat: minLat, lng: minLon },
    { lat: maxLat, lng: maxLon }
  );
  return bounds.intersects(visibleBounds) && item.id;
};

const useDemographicRegionsBoundaries = (
  countryCode: CountryCode,
  onlyVisibleRegions: boolean,
  viewport: MapViewportState
): [currentLevel: TerritoryLevel, regions: LevelAreaRegions] => {
  const { map } = useMap();
  const [zoom, setZoom] = useState(map.getZoom()!);
  useMapEventHandler("zoom_changed", () => {
    setZoom(map.getZoom()!);
  });

  const [adm2Threshold, adm3Threshold] = defaultThresholdLevels;
  const currentLevel = useMemo(
    () => {
      if (zoom < adm2Threshold) {
        return TerritoryLevel.ADM1;
      } else if (zoom < adm3Threshold) {
        return TerritoryLevel.ADM2;
      } else {
        return TerritoryLevel.ADM3;
      }
    },
    // we use spread to not have a dep on thresholdZoomLevels array instance, we only care about values
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [zoom, ...defaultThresholdLevels]
  );

  const adm1 = `${countryCode}_${TerritoryLevel.ADM1}` as AreaType;
  const [adm1MetaData] = useAreaTypeMetaData(adm1);
  const [adm1Regions] = useBoundaries(adm1, adm1MetaData.files);

  const adm2 = `${countryCode}_${TerritoryLevel.ADM2}` as AreaType;
  const [adm2MetaData] = useAreaTypeMetaData(adm2, zoom >= adm2Threshold);
  const [adm2Regions] = useBoundaries(adm2, adm2MetaData.files, zoom >= adm2Threshold);

  const adm3 = `${countryCode}_${TerritoryLevel.ADM3}` as AreaType;
  const [adm3MetaData] = useAreaTypeMetaData(adm3, zoom >= adm3Threshold);
  const [adm3Regions] = useBoundaries(adm3, adm3MetaData.files, zoom >= adm3Threshold);

  const visibleBounds = viewport.bounds
    ? convertPlatformBoundsToLatLngBounds(viewport.bounds)
    : undefined;

  const adm1Result = useMemo(
    () =>
      onlyVisibleRegions && visibleBounds
        ? adm1Regions.filter(filterVisibleRegions(visibleBounds))
        : adm1Regions,
    // adm1Regions will change when view port or zoom is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adm1Regions, onlyVisibleRegions]
  );

  const adm2Result = useMemo(
    () => {
      return zoom >= adm2Threshold
        ? onlyVisibleRegions && visibleBounds
          ? adm2Regions.filter(filterVisibleRegions(visibleBounds))
          : adm2Regions
        : [];
    },
    // adm2Regions will change when view port or zoom is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adm2Regions, onlyVisibleRegions]
  );

  const adm3Result = useMemo(
    () =>
      zoom >= adm3Threshold
        ? onlyVisibleRegions && visibleBounds
          ? adm3Regions.filter(filterVisibleRegions(visibleBounds))
          : adm3Regions
        : [],
    // adm3Regions will change when view port or zoom is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adm3Regions, onlyVisibleRegions]
  );

  return [
    currentLevel,
    {
      adm1: adm1Result,
      adm2: adm2Result,
      adm3: adm3Result,
    },
  ];
};

export default useDemographicRegionsBoundaries;
